
import { CircleIcon, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk";
import { Loading, UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSession, MeetingSessionHighlightingState } from "@kalyzee/kast-web-sdk/websocket";
import React, { useCallback, useMemo } from "react";
import { useMeetingUtilsContext } from "../../../contexts/meetingUtils/context";
import { PopulatedSession } from "../../../contexts/meetingUtils/interfaces";
import { getNormalUser } from "../../../helpers/user";
import { List } from "../../common/List";
import { SectionWorkBox } from "../SectionWorkBox";
import { SdkContextState, useSdkContext } from "../../../contexts/sdk/context";
import { toastError } from "../../../helpers/toast";
import { Button } from "../../buttons";
import { StringInterval } from "../../utils/StringInterval";
import { HighlightedSessions } from "../../../interfaces/highlight";

import styles from "./HighlightedUserListWorkBox.module.css";

const Item = React.memo(({ 
  session,
  sessionIncr,
  extra,
  subtitle,
}: { 
  session: MeetingSession,
  sessionIncr: number;
  extra: React.ReactNode,
  subtitle?: React.ReactNode,
}) => {
  const user = session.user;
  if (!user) return null;
  const { avatar, displayName } = getNormalUser(user);
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "0.5em",
      justifyContent: "space-between",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "0.5em",
      }}>
        <UserAvatar 
          mode="image"
          image={avatar}
          username={displayName}
          style={{ height: "2.5em", width: "2.5em" }}
        />
        <div style={{ display: "flex", flexDirection: "column", fontSize: "1.2em", lineHeight: "1em" }}>
          {displayName}{sessionIncr > 0 ? ` (${sessionIncr})` : ""}
          {subtitle && (
            <span style={{ fontSize: "0.7em", color: "var(--color-gray-1)" }}>
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {extra}
    </div>
  );
});

export interface HighlightedUserListProps {
  sdk: SdkContextState,
  highlightSessions: HighlightedSessions;
  style?: React.CSSProperties,
  foldableSections?: boolean;
  enabledSections?: {
    requesting?: boolean;
    ongoing?: boolean;
    inactive?: boolean;
  },
  onClose?: () => void;
}

// un hook dans meeting.tsx qui créé la data de la box
// un deuxième hook qui observe cet data et lance une popup si besoin

export const HighlightedUserListWorkBox = React.memo(({
  sdk,
  highlightSessions,
  style,
  foldableSections,
  enabledSections = {
    requesting: true,
    ongoing: true,
    inactive: true,
  },
  onClose,
}: HighlightedUserListProps) => {
  const onHighlight = useCallback(async (session: PopulatedSession) => {
    const client = sdk.client.getWebsocketClient();
    if (!client) return;

    const { error, response } = await client.emitter.meeting.startHighlighting({
      sessionId: session.id,
    });

    if (error || !response) {
      if (!error?.message.includes("is already ongoing")) {
        toastError("Une erreur est apparue.");
        return;
      }
    }
  }, [sdk]);

  const onRemoveHighlight = useCallback(async (session: PopulatedSession) => {
    const client = sdk.client.getWebsocketClient();
    if (!client) return;

    const { error, response } = await client.emitter.meeting.stopHighlighting({
      sessionId: session.id,
    });
    
    if (error || !response) {
      toastError("Une erreur est apparue.");
    }
  }, [sdk]);

  const onAnswerRequest = useCallback(async (session: PopulatedSession, accepted: boolean) => {
    const client = sdk.client.getWebsocketClient();
    if (!client) return;

    const { error, response } = await client.emitter.meeting.respondToTheRequestForHighlighting({
      sessionId: session.id,
      accepted,
    });
    
    if (error || !response) {
      toastError("Une erreur est apparue.");
    }
  }, [sdk]);

  const renderRequestsSection = () => {
    return (
      <List
        style={{ padding: "1em" }}
        items={highlightSessions.requesting}
        renderItem={session => (
          <Item
            session={session.session}
            sessionIncr={session.increment}
            extra={
              <div style={{ display: "flex", gap: "0.5em", fontSize: "0.8em" }}>
                <PrimaryButton onClick={() => onAnswerRequest(session, true)}>
                  <CircleIcon.Variants.ChoiceOK />
                </PrimaryButton>
                <PrimaryButton onClick={() => onAnswerRequest(session, false)}>
                  <CircleIcon.Variants.ChoiceKO />
                </PrimaryButton>
              </div>
            }
          />
        )}
        emptyPlaceholder={"Aucune demande"}
      />
    );
  };

  const renderOngoingSection = () => {
    return (
      <List
        style={{ padding: "1em" }}
        items={highlightSessions.ongoing}
        renderItem={session => (
          <Item
            session={session.session.session}
            sessionIncr={session.session.increment}
            extra={
              <Button.Simple 
                onClick={() => onRemoveHighlight(session.session)}
                label="Retirer"
                className={styles.removeBtn}
              />
            }
            subtitle={session.pending ? (
              <span>En attente de confirmation <StringInterval delay={500} strings={["", ".", "..", "..."]} /></span>
            ) : null}
          />
        )}
        emptyPlaceholder="Aucun intervenant"
      />
    );
  };

  const renderOthersSection = () => {
    return (
      <List
        style={{ padding: "1em" }}
        items={highlightSessions.inactive}
        renderItem={session => (
          <Item
            session={session.session}
            sessionIncr={session.increment}
            extra={
              <Button.Simple
                label="Ajouter"
                onClick={() => onHighlight(session)}
              />
            }
          />
        )}
        emptyPlaceholder="Aucun utilisateur disponible"
      />
    );
  };

  const sections = useMemo(() => {
    const _sections = [];
    if (enabledSections.requesting) {
      _sections.push({
        header: `Demandes d'intervention (${highlightSessions.requesting.length})`,
        left: <ReactIcons.Hand2 style={{ width: "1.5em", height: "1.5em" }} />,
        content: renderRequestsSection(),
      });
    }
    if (enabledSections.ongoing) {
      _sections.push({
        header: `Interventions en cours (${highlightSessions.ongoing.length})`,
        content: renderOngoingSection(),
      });
    }
    if (enabledSections.inactive) {
      _sections.push({
        header: `Inviter des intervenants (${highlightSessions.inactive.length})`,
        content: renderOthersSection(),
      });
    }
    return _sections;
  }, [enabledSections, highlightSessions]);
    
  return (
    <SectionWorkBox
      style={style}
      foldable={foldableSections}
      sections={sections}
      onClose={onClose}
    />
  );
});